/**
 * SCRUM-216 base + SCRUM-228: premios sobre cupones/promos existentes.
 * Preferir `existing_coupon` / `existing_promotion` / `template` (alias) antes que
 * emitir cupones one-shot aislados (`percent`/`fixed`).
 */

export const DISCOUNT_WHEEL_CAPABILITY = "discount_wheel" as const;

export type DiscountWheelPrizeKind =
  | "percent"
  | "fixed"
  | "noop"
  | "template"
  | "existing_coupon"
  | "existing_promotion";

export type DiscountWheelPrize = {
  id: string;
  label: string;
  /** Peso relativo para el sorteo (≥ 0). */
  weight: number;
  kind: DiscountWheelPrizeKind;
  /** percent: 1–100 · fixed: monto en unidad de moneda · template/existing: ignorado */
  value?: number;
  /** kind=template|existing_coupon → id de cupón existente */
  templateCouponId?: string | null;
  /** kind=existing_promotion → id de promoción existente */
  promotionId?: string | null;
};

export type DiscountWheelConfig = {
  enabled: boolean;
  requireEmail: boolean;
  headline: string;
  prizes: DiscountWheelPrize[];
};

export const DEFAULT_DISCOUNT_WHEEL_PRIZES: DiscountWheelPrize[] = [
  { id: "p5", label: "5% OFF", weight: 40, kind: "percent", value: 5 },
  { id: "p10", label: "10% OFF", weight: 25, kind: "percent", value: 10 },
  { id: "p15", label: "15% OFF", weight: 10, kind: "percent", value: 15 },
  { id: "noop", label: "Seguí participando", weight: 25, kind: "noop" },
];

export const DEFAULT_DISCOUNT_WHEEL_CONFIG: DiscountWheelConfig = {
  enabled: false,
  requireEmail: true,
  headline: "¡Girá la ruleta!",
  prizes: DEFAULT_DISCOUNT_WHEEL_PRIZES,
};

export function normalizeDiscountWheelPrizes(raw: unknown): DiscountWheelPrize[] {
  if (!Array.isArray(raw) || raw.length === 0) return [...DEFAULT_DISCOUNT_WHEEL_PRIZES];
  const out: DiscountWheelPrize[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const o = item as Record<string, unknown>;
    const id = typeof o.id === "string" && o.id.trim() ? o.id.trim() : `p${out.length + 1}`;
    const label = typeof o.label === "string" ? o.label.trim().slice(0, 80) : "";
    const weight = Number(o.weight ?? 1);
    const kindRaw = String(o.kind ?? "percent");
    let kind = (["percent", "fixed", "noop", "template", "existing_coupon", "existing_promotion"].includes(kindRaw)
      ? kindRaw
      : "percent") as DiscountWheelPrizeKind;
    // Alias histórico SCRUM-216
    if (kind === "template") kind = "existing_coupon";
    if (!label || !Number.isFinite(weight) || weight < 0) continue;
    const value = o.value != null ? Number(o.value) : undefined;
    out.push({
      id,
      label,
      weight,
      kind,
      value: value != null && Number.isFinite(value) ? value : undefined,
      templateCouponId:
        typeof o.templateCouponId === "string" ? o.templateCouponId : null,
      promotionId: typeof o.promotionId === "string" ? o.promotionId : null,
    });
  }
  return out.length > 0 ? out : [...DEFAULT_DISCOUNT_WHEEL_PRIZES];
}

export function canShowDiscountWheel(opts: {
  discountWheelEnabled: boolean;
  discountCouponsEnabled?: boolean;
  configEnabled: boolean;
}): boolean {
  if (!opts.discountWheelEnabled) return false;
  if (opts.discountCouponsEnabled === false) return false;
  return opts.configEnabled;
}

/** Soft connect a growth_campaigns: trigger.wheel activo refuerza show. */
export function shouldActivateWheelFromCampaigns(
  campaigns: ReadonlyArray<{ status?: string; trigger?: { type?: string } }>,
): boolean {
  return campaigns.some(
    (c) => c.status === "active" && c.trigger?.type === "wheel",
  );
}

/**
 * Weighted random pick. `rng` in [0,1).
 */
export function pickDiscountWheelPrize(
  prizes: readonly DiscountWheelPrize[],
  rng: number = Math.random(),
): DiscountWheelPrize {
  const list = prizes.length ? prizes : DEFAULT_DISCOUNT_WHEEL_PRIZES;
  const total = list.reduce((s, p) => s + Math.max(0, p.weight), 0);
  if (total <= 0) return list[0]!;
  let cursor = Math.min(Math.max(rng, 0), 0.999999) * total;
  for (const p of list) {
    cursor -= Math.max(0, p.weight);
    if (cursor < 0) return p;
  }
  return list[list.length - 1]!;
}

export function generateWheelCouponCode(prefix = "WHEEL"): string {
  const rand = Math.random().toString(36).slice(2, 8).toUpperCase();
  return `${prefix}${rand}`.slice(0, 16);
}

/** Shape mínimo para dbSmUpsertCoupon (one-shot). */
export function buildOneShotCouponFromPrize(opts: {
  tenantId: string;
  prize: DiscountWheelPrize;
  code?: string;
  expiresInHours?: number;
}): {
  ok: true;
  input: {
    tenantId: string;
    code: string;
    name: string;
    description: string;
    type: "percentage" | "fixed";
    value: number;
    appliesTo: "all";
    minOrderCents: number;
    maxUses: number;
    perUserLimit: number;
    startsAt: string | null;
    expiresAt: string;
    active: boolean;
    showPopup: boolean;
    popupImageUrl: null;
    popupTitle: string;
    popupBody: string;
    popupCta: string;
    productIds: string[];
    categoryIds: string[];
  };
} | { ok: false; reason: "noop" | "invalid_prize" } {
  const { prize } = opts;
  if (prize.kind === "noop") return { ok: false, reason: "noop" };
  if (
    prize.kind === "template" ||
    prize.kind === "existing_coupon" ||
    prize.kind === "existing_promotion"
  ) {
    // Premios sobre inventario existente: no generar cupón one-shot.
    return { ok: false, reason: "invalid_prize" };
  }
  const value = Number(prize.value ?? 0);
  if (!Number.isFinite(value) || value <= 0) return { ok: false, reason: "invalid_prize" };
  if (prize.kind === "percent" && value > 100) return { ok: false, reason: "invalid_prize" };

  const hours = opts.expiresInHours ?? 72;
  const expiresAt = new Date(Date.now() + hours * 3600_000).toISOString();
  const code = (opts.code ?? generateWheelCouponCode()).toUpperCase();

  return {
    ok: true,
    input: {
      tenantId: opts.tenantId,
      code,
      name: `Ruleta: ${prize.label}`,
      description: "Cupón one-shot emitido por discount wheel",
      type: prize.kind === "fixed" ? "fixed" : "percentage",
      value,
      appliesTo: "all",
      minOrderCents: 0,
      maxUses: 1,
      perUserLimit: 1,
      startsAt: null,
      expiresAt,
      active: true,
      showPopup: false,
      popupImageUrl: null,
      popupTitle: "",
      popupBody: "",
      popupCta: "Usar cupón",
      productIds: [],
      categoryIds: [],
    },
  };
}

export function publicPrizesForClient(
  prizes: readonly DiscountWheelPrize[],
): Array<{ id: string; label: string }> {
  return prizes.map((p) => ({ id: p.id, label: p.label }));
}

// ─── Anti-abuse claim key (pure) ──────────────────────────────────────────────

/**
 * claim_key = lower(email) o visitor id. Null si no hay identificador usable.
 */
export function resolveDiscountWheelClaimKey(opts: {
  email?: string | null;
  visitorId?: string | null;
}): string | null {
  const email = typeof opts.email === "string" ? opts.email.trim().toLowerCase() : "";
  if (email && email.includes("@")) return email;
  const vid = typeof opts.visitorId === "string" ? opts.visitorId.trim() : "";
  if (vid) return `vid:${vid}`;
  return null;
}

/** Extrae visitor id de header `x-sm-visitor-id` / `x-visitor-id` o cookie `sm.funnel.vid`. */
export function extractVisitorIdFromRequest(request: Request): string | null {
  const h =
    request.headers.get("x-sm-visitor-id")?.trim() ||
    request.headers.get("x-visitor-id")?.trim() ||
    "";
  if (h) return h.slice(0, 128);
  const cookie = request.headers.get("cookie") ?? "";
  const m = cookie.match(/(?:^|;\s*)sm\.funnel\.vid=([^;]+)/);
  if (m?.[1]) {
    try {
      return decodeURIComponent(m[1].trim()).slice(0, 128);
    } catch {
      return m[1].trim().slice(0, 128);
    }
  }
  return null;
}

/** True si ya existe claim → respuesta already_claimed. */
export function isDiscountWheelAlreadyClaimed(existing: unknown): boolean {
  return existing != null;
}


/** Validación pre-entrega (sin carrito) para cupón existente como premio. */
export function isCouponEligibleAsWheelPrize(coupon: {
  active: boolean;
  startsAt?: string | null;
  expiresAt?: string | null;
  maxUses?: number | null;
  usedCount?: number | null;
}): { ok: true } | { ok: false; code: string; error: string } {
  const now = Date.now();
  if (!coupon.active) return { ok: false, code: "inactive", error: "Cupón inactivo" };
  if (coupon.startsAt && new Date(coupon.startsAt).getTime() > now) {
    return { ok: false, code: "not_started", error: "Cupón aún no vigente" };
  }
  if (coupon.expiresAt && new Date(coupon.expiresAt).getTime() < now) {
    return { ok: false, code: "expired", error: "Cupón vencido" };
  }
  if (
    coupon.maxUses != null &&
    coupon.usedCount != null &&
    coupon.usedCount >= coupon.maxUses
  ) {
    return { ok: false, code: "exhausted", error: "Cupón sin usos disponibles" };
  }
  return { ok: true };
}

export function isPromotionEligibleAsWheelPrize(promo: {
  active: boolean;
  startsAt?: string | null;
  expiresAt?: string | null;
}): { ok: true } | { ok: false; code: string; error: string } {
  const now = Date.now();
  if (!promo.active) return { ok: false, code: "inactive", error: "Promoción inactiva" };
  if (promo.startsAt && new Date(promo.startsAt).getTime() > now) {
    return { ok: false, code: "not_started", error: "Promoción aún no vigente" };
  }
  if (promo.expiresAt && new Date(promo.expiresAt).getTime() < now) {
    return { ok: false, code: "expired", error: "Promoción vencida" };
  }
  return { ok: true };
}

export function prizeOriginLabel(prize: DiscountWheelPrize): string {
  if (prize.kind === "existing_coupon" || prize.kind === "template") return "Cupón existente";
  if (prize.kind === "existing_promotion") return "Promoción existente";
  if (prize.kind === "noop") return "Sin premio";
  if (prize.kind === "percent" || prize.kind === "fixed") return "Cupón nuevo (ruleta)";
  return prize.kind;
}
